import React, { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Check, Loader2, Search, Star, Upload } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { MediaThumb } from '@components/media/MediaThumb';
import { MediaUploadDialog } from '@components/media/MediaUploadDialog';
import { usePermissions } from '@hooks/usePermissions';
import mediaService from '@services/mediaService';
import type { MediaAsset, MediaKind } from '@/types/media';
import type { MediaBrief } from '@/types/social';
import { toBrief } from '@/utils/social';

const PAGE_SIZE = 40;

interface MediaPickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onPick: (media: MediaBrief[]) => void;
  /** Tipos aceitos pelo formato (vazio = todos). */
  kinds?: MediaKind[];
  /** Quantas ainda cabem (0 = sem limite). */
  max?: number;
  title?: string;
  /** Texto do botão de confirmar (padrão: posts de rede social). */
  confirmText?: string;
  /** Onde a ordem do clique vale (padrão: "no post"). */
  orderHint?: string;
  /** Outra biblioteca para escolher (o editor de e-mail de HML usa a de produção). Só fotos. */
  altSource?: MediaPickerAltSource;
}

export interface MediaPickerAltSource {
  label: string;
  localLabel: string;
  hint: string;
  list: (query: string, page: number, pageSize: number) => Promise<{ items: MediaAsset[]; total: number }>;
}

/**
 * Seletor da Biblioteca de Mídias para os posts: a mesma busca da biblioteca (texto, tags e IA),
 * filtro de tipo e preferenciais, seleção múltipla na ordem do clique. Arquivo novo entra pela
 * própria biblioteca (envio + catalogação) e aparece aqui para escolher.
 */
export const MediaPickerDialog: React.FC<MediaPickerDialogProps> = ({
  isOpen,
  onClose,
  onPick,
  kinds,
  max = 0,
  title,
  confirmText = 'Adicionar ao post',
  orderHint = 'a ordem do clique é a ordem no post',
  altSource,
}) => {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const onlyKind = kinds && kinds.length === 1 ? kinds[0] : '';
  const [kind, setKind] = useState<MediaKind | ''>(onlyKind);
  const [preferred, setPreferred] = useState(false);
  const [items, setItems] = useState<MediaAsset[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<MediaAsset[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [useAlt, setUseAlt] = useState(false);
  const alt = useAlt && !!altSource;
  const { checkPermission } = usePermissions();
  const canUpload = checkPermission('media.upload').hasPermission;

  useEffect(() => {
    if (isOpen) {
      setSelected([]);
      setKind(onlyKind);
    }
  }, [isOpen, onlyKind]);

  const load = useCallback(
    async (p: number) => {
      setLoading(true);
      try {
        if (alt && altSource) {
          const res = await altSource.list(query, p, PAGE_SIZE);
          setItems((prev) => (p === 1 ? res.items : [...prev, ...res.items]));
          setTotal(res.total);
        } else {
          const res = await mediaService.list({ search: query || undefined, kind, preferred: preferred || undefined, page: p, page_size: PAGE_SIZE });
          setItems((prev) => (p === 1 ? res.data : [...prev, ...res.data]));
          setTotal(res.pagination.total_items);
        }
        setPage(p);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [query, kind, preferred, alt, altSource],
  );

  useEffect(() => {
    if (isOpen) load(1);
  }, [isOpen, load]);

  const toggle = (m: MediaAsset) => {
    setSelected((s) => {
      if (s.some((x) => x.id === m.id)) return s.filter((x) => x.id !== m.id);
      if (max > 0 && s.length >= max) return s;
      return [...s, m];
    });
  };

  const allowed = (m: MediaAsset) => !kinds || kinds.length === 0 || kinds.includes(m.kind);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="full" closeOnBackdrop={false}>
        <div className="flex flex-col max-h-[90vh]">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-h3">{title ?? 'Escolher da Biblioteca de Mídias'}</h2>
            {altSource && (
              <div className="mt-2 inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
                {[false, true].map((v) => (
                  <button
                    key={String(v)}
                    type="button"
                    onClick={() => {
                      setUseAlt(v);
                      setSelected([]);
                    }}
                    className={clsx('px-3 py-1 text-sm rounded-md', useAlt === v ? 'bg-white shadow text-emerald-700 font-medium' : 'text-slate-600')}
                  >
                    {v ? altSource.label : altSource.localLabel}
                  </button>
                ))}
              </div>
            )}
            {alt && <p className="mt-1 text-xs text-emerald-700">{altSource!.hint}</p>}
            <p className="text-small text-slate-500">
              Busque como na biblioteca (texto, tags, descrição da IA). Clique para selecionar — {orderHint}.
              {max > 0 && ` Cabem mais ${max}.`}
            </p>
            <form
              className="mt-3 flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setQuery(search.trim());
              }}
            >
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="input-field w-full pl-8"
                  placeholder="closet planejado, cozinha, evento..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select className="select-field w-36" value={kind} onChange={(e) => setKind(e.target.value as MediaKind | '')} disabled={!!onlyKind || alt}>
                <option value="">Fotos e vídeos</option>
                <option value="image">Só fotos</option>
                <option value="video">Só vídeos</option>
              </select>
              <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                <input type="checkbox" className="checkbox-field" checked={preferred} disabled={alt} onChange={(e) => setPreferred(e.target.checked)} />
                <Star size={13} className="text-amber-500" /> Preferenciais
              </label>
              <Button type="submit" size="sm" variant="outline" icon={<Search />}>
                Buscar
              </Button>
              {canUpload && !alt && (
                <Button type="button" size="sm" variant="outline" icon={<Upload />} onClick={() => setUploadOpen(true)}>
                  Enviar arquivos
                </Button>
              )}
            </form>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
            {items.length === 0 && !loading && <p className="text-center text-slate-500 py-10">Nenhuma mídia encontrada.</p>}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8 gap-2">
              {items.map((m) => {
                const idx = selected.findIndex((x) => x.id === m.id);
                const ok = allowed(m);
                return (
                  <button
                    key={m.id}
                    type="button"
                    disabled={!ok}
                    onClick={() => toggle(m)}
                    title={ok ? m.title || m.original_file_name : 'Tipo não aceito por este formato'}
                    className={clsx(
                      'relative rounded-lg overflow-hidden border-2 text-left',
                      idx >= 0 ? 'border-emerald-500' : 'border-transparent hover:border-slate-300',
                      !ok && 'opacity-40 cursor-not-allowed',
                    )}
                  >
                    <MediaThumb media={m} className="aspect-square" />
                    {idx >= 0 && (
                      <span className="absolute top-1 left-1 w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center shadow">
                        {idx + 1}
                      </span>
                    )}
                    <span className="block truncate px-1 py-0.5 text-[11px] text-slate-600">{m.title || m.original_file_name}</span>
                  </button>
                );
              })}
            </div>
            {loading && (
              <div className="flex justify-center py-4">
                <Loader2 className="animate-spin text-slate-400" />
              </div>
            )}
            {!loading && items.length < total && (
              <div className="flex justify-center py-4">
                <Button size="sm" variant="outline" onClick={() => load(page + 1)}>
                  Carregar mais ({total - items.length})
                </Button>
              </div>
            )}
          </div>

          <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
            <span className="text-sm text-slate-600">{selected.length} selecionada(s)</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                icon={<Check />}
                disabled={selected.length === 0}
                onClick={() => {
                  onPick(selected.map((m) => ({ ...toBrief(m), fromProduction: alt || undefined })));
                  onClose();
                }}
              >
                {confirmText}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <MediaUploadDialog isOpen={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={() => load(1)} />
    </>
  );
};

export default MediaPickerDialog;
